import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LearningObjectivesStepShell from '@/components/step-shells/LearningObjectivesStepShell';
import { useCourse } from '@/contexts/CourseContext';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * ImportStep2Goals
 * Goals validation phase where the GoalsValidationAgent:
 * 1. Reviews imported goals
 * 2. Chats with user to refine/add goals
 * 3. Ensures goals are high-quality and aligned with course
 * 4. Transitions to activities planning (step 3)
 */
export default function ImportStep2Goals({ courseId, importSessionId, initialCourseData, onComplete, setCustomChatHandler, updateCourseData }) {
  const { dispatch } = useCourse();
  const [goals, setGoals] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingGoals, setIsGeneratingGoals] = useState(false);

  // CRITICAL: Set import mode when component mounts
  useEffect(() => {
    if (importSessionId) {
      dispatch({
        type: 'SET_IMPORT_MODE',
        payload: { sessionId: importSessionId, executionPlan: null }
      });
    }
  }, [importSessionId, dispatch]);

  // Initialize chat (clear history)
  useEffect(() => {
    const id = courseId || importSessionId;
    if (!id || initialized) return;
    setInitialized(true);
    dispatch({ type: 'SET_CHAT_HISTORY', payload: [] }); // Clear chat from previous step
  }, [courseId, importSessionId, initialized, dispatch]);

  // Load goals from initialCourseData & auto-generate if none exist (like build mode)
  useEffect(() => {
    if (!initialCourseData || initialized) return;

    const courseGoals = initialCourseData.goals || initialCourseData.learningObjectives || [];
    console.log('ImportStep2Goals: Loading goals from initialCourseData:', courseGoals.length);

    if (courseGoals.length > 0) {
      // Show loading state while checking for orphaned activities and generating goals
      setIsGeneratingGoals(true);

      // Show loading indicator in chat
      dispatch({
        type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          isLoading: true
        }
      });

      // Call /chat/step2 to trigger orphaned activity check and get complete course_data
      const checkOrphanedActivitiesAndGetGoals = async () => {
        try {
          const id = courseId || importSessionId;
          const response = await axios.post(`${API_URL}/chat/step2`, {
            courseId: id,
            message: `(System Instruction) I found ${courseGoals.length} imported learning goals. 
Please introduce this step to the user naturally. 
Mention that you found the goals from the file and we should review them.
Ask the user how they would like to proceed (e.g. review, edit, or add more).`
          });

          // Get unified goals array from response (includes both imported and generated goals)
          // SUPPORT BOTH SCHEMA FORMATS: 'goals' (legacy) and 'learningObjectives' (new)
          const allGoals = response.data.course_data?.learningObjectives ||
            response.data.course_data?.goals ||
            courseGoals;

          console.log('ImportStep2Goals: Received goals from backend:', {
            totalGoals: allGoals.length,
            importedCount: allGoals.filter(g => g.source === 'imported').length,
            generatedCount: allGoals.filter(g => g.source === 'generated').length,
            goals: allGoals.map(g => ({ id: g.id, text: g.text?.substring(0, 50), source: g.source }))
          });

          // Update goals state with unified array
          setGoals(allGoals);

          // Update course data in context if available
          if (response.data.course_data) {
            dispatch({ type: 'SET_COURSE', payload: response.data.course_data });
          }

          // Remove loading indicator and add agent response
          dispatch({ type: 'REMOVE_LAST_MESSAGE' });
          dispatch({
            type: 'ADD_CHAT_MESSAGE', payload: {
              role: 'assistant',
              text: response.data.response || `Βρήκα ${courseGoals.length} εισαγόμενους στόχους. Ας ελέγξουμε και βελτιώσουμε τους στόχους σας.\n\nΜπορώ να σας βοηθήσω να επεξεργαστείτε, να προσθέσετε ή να αφαιρέσετε στόχους. Από πού θέλετε να ξεκινήσουμε;`
            }
          });

          // Allow rendering of goals
          setIsGeneratingGoals(false);
        } catch (error) {
          console.error('Error checking orphaned activities:', error);
          // Fallback to imported goals if request fails
          setGoals(courseGoals);
          dispatch({ type: 'REMOVE_LAST_MESSAGE' });
          dispatch({
            type: 'ADD_CHAT_MESSAGE', payload: {
              role: 'assistant',
              text: `Βρήκα ${courseGoals.length} εισαγόμενους στόχους. Ας ελέγξουμε και βελτιώσουμε τους στόχους σας.\n\nΜπορώ να σας βοηθήσω να επεξεργαστείτε, να προσθέσετε ή να αφαιρέσετε στόχους. Από πού θέλετε να ξεκινήσουμε;`
            }
          });
          setIsGeneratingGoals(false);
        }
      };

      checkOrphanedActivitiesAndGetGoals();
    } else {
      // NO GOALS - Auto-generate from activities (like build mode does)
      const hasActivities = initialCourseData.topics && initialCourseData.topics.some(t => t.activities && t.activities.length > 0);

      if (hasActivities) {
        console.log('ImportStep2Goals: No goals found, auto-generating from activities...');
        setIsGeneratingGoals(true); // START LOADING

        dispatch({
          type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            text: '🎯 Δημιουργώ στόχους από τις δραστηριότητες...'
          }
        });

        // Call backend to generate goals from activities
        setTimeout(async () => {
          try {
            const id = courseId || importSessionId;
            const response = await axios.post(`${API_URL}/transition/step1-to-step2`, {
              courseId: id
            });

            if (response.data.course_data) {
              // SUPPORT BOTH SCHEMA FORMATS
              const generatedGoals = response.data.course_data.learningObjectives ||
                response.data.course_data.goals ||
                [];
              console.log('ImportStep2Goals: Generated goals received:', generatedGoals); // DEBUG LOG

              setGoals(generatedGoals);
              dispatch({ type: 'SET_COURSE', payload: response.data.course_data });

              // Get dynamic welcome from agent after generation
              // Keep loading indicator while fetching agent response

              const welcomeResponse = await axios.post(`${API_URL}/chat/step2`, {
                courseId: id,
                message: `(System Instruction) The system has just auto-generated ${generatedGoals.length} learning goals based on the course activities.
Please introduce these goals to the user naturally.
Explain that since explicit goals weren't found, you've drafted some initial ones based on the content.
Invite the user to review and refine them.`
              });

              dispatch({ type: 'REMOVE_LAST_MESSAGE' });
              dispatch({
                type: 'ADD_CHAT_MESSAGE', payload: {
                  role: 'assistant',
                  text: welcomeResponse.data.response || `✅ Δημιούργησα ${generatedGoals.length} στόχους από τις δραστηριότητες!`
                }
              });
            }
          } catch (error) {
            console.error('Failed to auto-generate goals:', error);
            dispatch({ type: 'REMOVE_LAST_MESSAGE' });
            dispatch({
              type: 'ADD_CHAT_MESSAGE', payload: {
                role: 'assistant',
                text: 'Δεν βρέθηκαν στόχοι. Ας δημιουργήσουμε μαθησιακούς στόχους μαζί!'
              }
            });
          } finally {
            setIsGeneratingGoals(false); // STOP LOADING
          }
        }, 100);
      } else {
        // No goals and no activities - show loading then get dynamic welcome
        dispatch({
          type: 'ADD_CHAT_MESSAGE', payload: {
            role: 'assistant',
            isLoading: true
          }
        });

        const getAgentWelcome = async () => {
          try {
            const id = courseId || importSessionId;
            const response = await axios.post(`${API_URL}/chat/step2`, {
              courseId: id,
              message: "Let's create the learning goals. What should we focus on?"
            });

            dispatch({ type: 'REMOVE_LAST_MESSAGE' });
            dispatch({
              type: 'ADD_CHAT_MESSAGE', payload: {
                role: 'assistant',
                text: response.data.response || 'Ας ορίσουμε τους μαθησιακούς στόχους.\n\nΘα καθορίσουμε τι θα πρέπει να μπορούν να κάνουν οι φοιτητές μετά την ολοκλήρωση του μαθήματός σας.\n\nΜπορώ να σας βοηθήσω να δημιουργήσετε, να επεξεργαστείτε και να βελτιώσετε τους στόχους σας. Από πού θέλετε να ξεκινήσουμε;'
              }
            });
          } catch (error) {
            console.error('Error getting agent welcome:', error);
            dispatch({ type: 'REMOVE_LAST_MESSAGE' });
            dispatch({
              type: 'ADD_CHAT_MESSAGE', payload: {
                role: 'assistant',
                text: 'Ας ορίσουμε τους μαθησιακούς στόχους.\n\nΘα καθορίσουμε τι θα πρέπει να μπορούν να κάνουν οι φοιτητές μετά την ολοκλήρωση του μαθήματός σας.\n\nΜπορώ να σας βοηθήσω να δημιουργήσετε, να επεξεργαστείτε και να βελτιώσετε τους στόχους σας. Από πού θέλετε να ξεκινήσουμε;'
              }
            });
          }
        };
        getAgentWelcome();
      }
    }
  }, [initialCourseData, initialized, dispatch, courseId, importSessionId]);

  // Custom send handler for goals validation
  const handleSendMessage = useCallback(async (userMessage) => {
    if (!userMessage.trim() || isProcessing) return;

    setIsProcessing(true);
    dispatch({ type: 'SET_LOADING', payload: true });

    // Remove any existing loading indicators first to prevent duplicates
    dispatch({ type: 'REMOVE_LOADING_MESSAGES' });

    // Add user message to chat
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'user', text: userMessage } });

    // Add typing indicator
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: { role: 'assistant', isLoading: true } });

    try {
      // Use unified chat endpoint for Step 2
      const id = courseId || importSessionId;
      const response = await axios.post(`${API_URL}/chat/step2`, {
        courseId: id,
        message: userMessage
      });

      // Remove typing indicator
      dispatch({ type: 'REMOVE_LAST_MESSAGE' });

      // Add assistant response
      const assistantText = response.data.response || response.data.message || ' ';
      dispatch({
        type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          text: assistantText
        }
      });

      // Update goals from backend response
      // SUPPORT BOTH SCHEMA FORMATS: 'goals' (legacy) and 'learningObjectives' (new)
      if (response.data.course_data) {
        const newGoals = response.data.course_data.learningObjectives ||
          response.data.course_data.goals;
        if (newGoals) {
          setGoals(newGoals);
        }
      }

      // Check if step is complete - proceed to activities
      if (response.data.step_complete || response.data.next_step === 3) {
        if (onComplete) {
          onComplete({
            goals: goals
          });
        }
        return;
      }

    } catch (error) {
      console.error('Error sending message:', error);
      // Remove typing indicator
      dispatch({ type: 'REMOVE_LAST_MESSAGE' });
      dispatch({
        type: 'ADD_CHAT_MESSAGE', payload: {
          role: 'assistant',
          text: '❌ Συγγνώμη, παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.'
        }
      });
    } finally {
      setIsProcessing(false);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [courseId, importSessionId, goals, isProcessing, onComplete, dispatch]);

  // Register custom chat handler with parent using Ref pattern to avoid infinite loops
  const handlerRef = React.useRef(handleSendMessage);
  useEffect(() => {
    handlerRef.current = handleSendMessage;
  });

  useEffect(() => {
    if (setCustomChatHandler) {
      setCustomChatHandler(() => (msg) => handlerRef.current(msg));
    }
    return () => {
      if (setCustomChatHandler) {
        setCustomChatHandler(null);
      }
    };
  }, [setCustomChatHandler]);

  // Normalize goals to have text and id properties
  // Backend should always return goals as objects with id and text, but handle edge cases defensively
  const normalizedGoals = goals
    .filter((goal) => {
      // Filter out invalid goals (just UUIDs or missing text)
      if (typeof goal === 'string') {
        // If goal is just a string (UUID), it's invalid - skip it
        console.warn('Skipping invalid goal that is just a string/UUID:', goal);
        return false;
      }
      if (typeof goal === 'object' && goal.id && !goal.text) {
        // If goal has id but no text, it's invalid - skip it
        console.warn('Skipping invalid goal missing text field:', goal);
        return false;
      }
      return true;
    })
    .map((goal, idx) => ({
      id: goal.id || goal.Id || (idx + 1),
      text: goal.text || goal.Text || goal.description || goal.Description || 'Unknown goal',
      bloomLevel: goal.bloomLevel || goal.bloom_level || goal.BloomLevel || 'Understanding',
      source: goal.source || null, // Preserve source field for badge display ('imported' or 'generated')
      isProtected: goal.isProtected || false,
      linkedActivityCount: goal.linkedActivityCount || 0
    }));

  const handleEdit = async (path, value) => {
    if (!updateCourseData) return;
    const pathParts = path.split('.');
    const updatedGoals = JSON.parse(JSON.stringify(goals));

    let current = { learningObjectives: updatedGoals };
    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];
      const nextPart = pathParts[i + 1];
      const index = parseInt(nextPart);

      if (!isNaN(index)) {
        current = current[part][index];
        i++;
      } else {
        current = current[part];
      }
    }

    const lastKey = pathParts[pathParts.length - 1];
    current[lastKey] = value;

    await updateCourseData({ goals: updatedGoals });
    setGoals(updatedGoals);
  };

  return (
    <>
      {isGeneratingGoals ? (
        <div className="w-full flex-1 p-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-teal-100 text-center">
              <div className="flex flex-col items-center justify-center gap-4">
                <Loader2 className="animate-spin text-teal-500" size={32} />
                <p className="text-gray-700 font-medium">Generating Goals...</p>
                <p className="text-sm text-gray-500">Checking for orphaned activities and generating goals</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <LearningObjectivesStepShell
          objectives={normalizedGoals}
          headerTitle="Έλεγχος & Οριστικοποίηση Στόχων"
          headerSubtitle="Ελέγξτε και βελτιώστε τους μαθησιακούς στόχους του μαθήματός σας"
          onEdit={handleEdit}
          importMode={true}
        />
      )}
    </>
  );
}
